import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchMembers, inviteMember, updateMember, removeMember } from '../services/api';
import type { WorkspaceMember } from '../types';
import Button from '../components/Button';
import Chip from '../components/Chip';
import Avatar from '../components/Avatar';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { useAppStore } from '../store/useAppStore';

function formatBytes(bytes: number): string {
  const n = typeof bytes === 'string' ? parseInt(bytes) : bytes;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(0)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

const GB = 1024 * 1024 * 1024;

export default function Members() {
  const { toast } = useAppStore();
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [showAllocate, setShowAllocate] = useState<WorkspaceMember | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviteQuota, setInviteQuota] = useState(50);
  const [allocateGB, setAllocateGB] = useState(50);

  const { data: members = [], isLoading } = useQuery({
    queryKey: qk.members(),
    queryFn: fetchMembers,
  });

  const inviteMutation = useMutation({
    mutationFn: () => inviteMember(inviteEmail, inviteRole, inviteQuota * GB),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.members() }); setShowInvite(false); setInviteEmail(''); toast('Invitation sent'); },
    onError: () => toast('Failed to send invitation', 'danger'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateMember>[1] }) => updateMember(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.members() }); setShowAllocate(null); toast('Member updated'); },
    onError: () => toast('Failed to update member', 'danger'),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => removeMember(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.members() }); toast('Member removed'); },
    onError: () => toast('Failed to remove member', 'danger'),
  });

  const totals = members.reduce((acc, m) => {
    acc.total++;
    if (m.role === 'admin') acc.admins++;
    acc.allocated += typeof m.quota_bytes === 'string' ? parseInt(m.quota_bytes) : 0;
    acc.used += m.used_bytes ?? 0;
    return acc;
  }, { total: 0, admins: 0, allocated: 0, used: 0 });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--line)', background: 'var(--card)' }}>
        <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>Members</h2>
        <Button kind="accent" icon="plus" onClick={() => setShowInvite(true)}>Invite member</Button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Members', value: totals.total, icon: 'users' },
            { label: 'Admins', value: totals.admins, icon: 'shield' },
            { label: 'Allocated', value: formatBytes(totals.allocated), icon: 'hard-drive' },
            { label: 'In use', value: formatBytes(totals.used), icon: 'database' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Icon name={s.icon} size={14} color="var(--ink-3)" />
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{s.label}</span>
              </div>
              <div style={{ fontSize: 'var(--fs-2xl)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Members table */}
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map(i => <div key={i} className="t-skeleton" style={{ height: 60, borderRadius: 8 }} />)}
          </div>
        ) : (
          <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
            <table className="t-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Storage</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => {
                  const quota = typeof m.quota_bytes === 'string' ? parseInt(m.quota_bytes) : 0;
                  const used = m.used_bytes ?? 0;
                  const pct = quota > 0 ? Math.min(100, (used / quota) * 100) : 0;
                  return (
                    <tr key={m.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar name={m.name ?? m.user_id} size="sm" />
                          <div>
                            <div style={{ fontWeight: 500, fontSize: 'var(--fs-sm)' }}>{m.name ?? m.user_id.slice(0, 12) + '…'}</div>
                            {m.email && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{m.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td><Chip tone={m.role === 'admin' ? 'accent' : 'neutral'}>{m.role}</Chip></td>
                      <td><Chip tone={m.status === 'active' ? 'success' : m.status === 'invited' ? 'warn' : 'neutral'} dot>{m.status}</Chip></td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 }}>
                          <div className="t-progress">
                            <div className={`t-progress-fill${pct > 90 ? ' t-progress-fill--danger' : ''}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>
                            {formatBytes(used)} / {formatBytes(quota)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Button kind="ghost" size="xs" icon="hard-drive" iconOnly onClick={() => { setShowAllocate(m); setAllocateGB(Math.round(quota / GB)); }} />
                          <Button kind="ghost" size="xs" icon="trash" iconOnly onClick={() => removeMutation.mutate(m.id)} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite modal */}
      <Modal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        title="Invite member"
        footer={
          <>
            <Button kind="ghost" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button kind="accent" loading={inviteMutation.isPending} onClick={() => inviteMutation.mutate()}>Send invitation</Button>
          </>
        }
      >
        <div className="t-stack">
          <div className="t-alert t-alert--info"><Icon name="info" size={13} />Identity is managed by the Softknack platform.</div>
          <div className="t-field">
            <label>Email</label>
            <input className="t-input" type="email" placeholder="user@company.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
          </div>
          <div className="t-field">
            <label>Role</label>
            <select className="t-input" value={inviteRole} onChange={e => setInviteRole(e.target.value as 'admin' | 'member')}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="t-field">
            <label>Storage allocation</label>
            <select className="t-input" value={inviteQuota} onChange={e => setInviteQuota(Number(e.target.value))}>
              <option value={10}>10 GB</option>
              <option value={50}>50 GB</option>
              <option value={100}>100 GB</option>
              <option value={200}>200 GB</option>
            </select>
          </div>
        </div>
      </Modal>

      {/* Allocate storage modal */}
      {showAllocate && (
        <Modal
          open
          onClose={() => setShowAllocate(null)}
          title={`Allocate storage — ${showAllocate.name ?? 'member'}`}
          size="sm"
          footer={
            <>
              <Button kind="ghost" onClick={() => setShowAllocate(null)}>Cancel</Button>
              <Button
                kind="accent"
                loading={updateMutation.isPending}
                onClick={() => updateMutation.mutate({ id: showAllocate.id, data: { quota_bytes: allocateGB * GB } })}
              >
                Save
              </Button>
            </>
          }
        >
          <div className="t-stack">
            <div className="t-field">
              <label>Quota (GB)</label>
              <input className="t-input" type="number" min={1} value={allocateGB} onChange={e => setAllocateGB(Number(e.target.value))} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[10, 50, 100].map(n => (
                <Button key={n} kind="outline" size="sm" onClick={() => setAllocateGB(v => v + n)}>+{n} GB</Button>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
